import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Scale, CalendarCheck, ShieldCheck, SlidersHorizontal, MousePointerClick, CalendarClock, Star, CheckCircle2, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useTitle, imageUrl } from "@/lib/format";
import SearchBar from "@/components/SearchBar";
import ProviderCard from "@/components/ProviderCard";
import { CategoryIcon, SectionHeading } from "@/components/common";

const HERO_IMG = "https://images.unsplash.com/photo-1646980241033-cd7abda2ee88?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80";
const HERO_IMG_2 = "https://images.unsplash.com/photo-1761718210089-ba3bb5ccb54f?crop=entropy&cs=srgb&fm=jpg&w=800&q=80";
const EXAMPLES = ["Home Cleaning", "Beauty & Wellness", "Photography", "Tutoring", "Home Maintenance", "Events", "Family Services", "Other Local Services"];

function Hero() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-slate-50 via-sky-50/40 to-white">
      <div className="hero-grid absolute inset-0 opacity-60" />
      <div className="relative mx-auto grid max-w-7xl gap-12 px-4 pb-16 pt-12 sm:px-6 md:pt-20 lg:grid-cols-[1.15fr_0.85fr] lg:px-8 lg:pb-24">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <p className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-sky-700">
            Discover. Compare. Book.
          </p>
          <h1 className="mt-6 font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl" data-testid="hero-headline">
            Find trusted local services and <span className="text-sky-600">book with ease.</span>
          </h1>
          <p className="mt-5 max-w-xl text-base text-slate-600 md:text-lg">
            Discover trusted professionals, compare services, and book the right service at a time that works for you.
          </p>
          <div className="mt-8 max-w-2xl"><SearchBar /></div>
          <div className="mt-5 flex flex-wrap gap-2" data-testid="hero-examples">
            {EXAMPLES.map((e) => (
              <Link key={e} to={`/services?q=${encodeURIComponent(e === "Other Local Services" ? "" : e)}`} className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:border-sky-300 hover:text-sky-700">
                {e}
              </Link>
            ))}
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }} className="relative hidden lg:block">
          <img src={HERO_IMG} alt="Local professional at work" className="h-[480px] w-full rounded-[28px] object-cover shadow-xl" />
          <img src={HERO_IMG_2} alt="" className="absolute -bottom-8 -left-10 h-40 w-40 rounded-2xl border-4 border-white object-cover shadow-lg" />
          <div className="absolute -right-4 top-8 rounded-2xl border border-slate-200 bg-white/95 p-4 shadow-lg backdrop-blur">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900"><Star className="h-4 w-4 fill-amber-400 text-amber-400" />4.8 average rating</div>
            <p className="mt-1 text-xs text-slate-500">from verified bookings</p>
          </div>
          <div className="absolute bottom-10 right-6 flex items-center gap-3 rounded-2xl border border-slate-200 bg-white/95 p-4 shadow-lg backdrop-blur">
            <CheckCircle2 className="h-8 w-8 text-teal-600" />
            <div><p className="text-sm font-semibold text-slate-900">Booking confirmed</p><p className="text-xs text-slate-500">Tue · 10:00 am · Deep Clean</p></div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function PopularServices({ categories }) {
  return (
    <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8" data-testid="popular-services">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <SectionHeading eyebrow="Browse" title="Popular Services" text="From a spotless home to the perfect portrait, find the right local expert." />
        <Link to="/categories" className="inline-flex items-center gap-1 text-sm font-semibold text-sky-700 hover:gap-2 transition-all" data-testid="all-categories-link">All categories <ArrowRight className="h-4 w-4" /></Link>
      </div>
      <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-4">
        {categories.map((c, i) => (
          <motion.div key={c.id} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className={i === 0 ? "col-span-2 md:row-span-2" : i === categories.length - 1 ? "col-span-2" : ""}>
            <Link to={`/services?category=${c.slug}`} data-testid={`category-card-${c.slug}`} className="group relative flex h-full min-h-[150px] flex-col justify-end overflow-hidden rounded-2xl bg-slate-900">
              <img src={imageUrl(c.image)} alt={c.name} className="absolute inset-0 h-full w-full object-cover opacity-70 transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/20 to-transparent" />
              <div className="relative p-4 sm:p-5">
                <span className="mb-2 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white/90 text-sky-700"><CategoryIcon name={c.icon} className="h-4 w-4" /></span>
                <p className="font-heading text-base font-semibold text-white sm:text-lg">{c.name}</p>
                <p className="text-xs text-white/75">{c.provider_count} providers</p>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

const STEPS = [
  [Search, "Discover", "Search by service and location to find trusted local professionals near you."],
  [Scale, "Compare", "Compare ratings, reviews, prices and availability side by side."],
  [CalendarCheck, "Book", "Pick a time that suits you and book online in under a minute."],
];

function HowItWorks() {
  return (
    <section className="border-y border-slate-200 bg-white py-20" data-testid="how-it-works">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading eyebrow="Simple" title="How Bookvera Works" />
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {STEPS.map(([Icon, title, text], i) => (
            <div key={title} className="relative rounded-2xl border border-slate-200 bg-slate-50/60 p-7">
              <span className="absolute right-6 top-5 font-heading text-5xl font-extrabold text-slate-200">0{i + 1}</span>
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-sky-600 text-white"><Icon className="h-5 w-5" /></span>
              <h3 className="mt-5 text-xl font-semibold text-slate-900">{i + 1}. {title}</h3>
              <p className="mt-2 text-sm text-slate-600">{text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const WHY = [
  [ShieldCheck, "Trusted local professionals", "Every business is reviewed by our team before it goes live."],
  [SlidersHorizontal, "Easy comparison", "Clear prices, durations, ratings and real availability."],
  [MousePointerClick, "Simple online booking", "No phone tag. Choose a time and book instantly."],
  [CalendarClock, "Convenient appointment management", "View, cancel or reschedule from My Bookings."],
];

function WhyBookvera() {
  return (
    <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8" data-testid="why-bookvera">
      <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr]">
        <SectionHeading eyebrow="Why us" title="Why Bookvera?" text="A calmer way to find and book the services your week depends on." />
        <div className="grid gap-4 sm:grid-cols-2">
          {WHY.map(([Icon, title, text]) => (
            <div key={title} className="rounded-2xl border border-slate-200 bg-white p-6 transition-shadow hover:shadow-md">
              <Icon className="h-6 w-6 text-teal-600" />
              <h3 className="mt-4 text-lg font-semibold text-slate-900">{title}</h3>
              <p className="mt-1.5 text-sm text-slate-600">{text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProviderCta() {
  return (
    <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
      <div className="relative overflow-hidden rounded-[28px] bg-slate-900 px-6 py-14 sm:px-12" data-testid="provider-cta">
        <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-sky-500/20 blur-3xl" />
        <div className="absolute -bottom-24 left-1/3 h-72 w-72 rounded-full bg-teal-500/20 blur-3xl" />
        <div className="relative max-w-2xl">
          <h2 className="font-heading text-3xl font-bold text-white sm:text-4xl">Grow your business with Bookvera</h2>
          <p className="mt-4 text-base text-slate-300 md:text-lg">Reach new customers, showcase your services, and manage your bookings in one place.</p>
          <Link to="/list-your-business" data-testid="cta-list-business" className="mt-8 inline-flex h-12 items-center gap-2 rounded-xl bg-white px-6 font-semibold text-slate-900 transition-transform hover:-translate-y-0.5">
            List Your Business <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  useTitle(null);
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data));
    api.get("/providers/featured").then(({ data }) => setFeatured(data));
  }, []);
  return (
    <div data-testid="home-page">
      <Hero />
      <PopularServices categories={categories} />
      <HowItWorks />
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8" data-testid="featured-providers">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <SectionHeading eyebrow="Featured" title="Featured Local Providers" text="Sample providers shown for this MVP are marked DEMO." />
          <Link to="/services" className="inline-flex items-center gap-1 text-sm font-semibold text-sky-700" data-testid="view-all-providers">View all <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {featured.map((p, i) => <ProviderCard key={p.id} provider={p} index={i} showBook={false} />)}
        </div>
      </section>
      <WhyBookvera />
      <ProviderCta />
    </div>
  );
}
